import { createAsyncThunk, createSlice, isFulfilled, isPending, isRejected } from "@reduxjs/toolkit";
import { PlanAssignmentSlice } from "Interface/interface";
import { apiRequest } from "services/ServiceUtil";
import { LIBRARY_SERVICE_METHOD, USER_PLAN_SERVICE_METHOD, USER_SERVICES_METHOD } from "services/commonService";
import { includeAboveProperty } from "utils/CommonConstants";

const SLICE = "PlanAssignment";

const isPlanAssignmentThunk = (action: any) => action.type.startsWith(`${SLICE}/`); // This is chech fullfilled and rejected cases only for PlanAssignment Slice.

const commonSteps = ["Assignment Preview", "Assignment Details", "Confirmation"];

const flowOptions: Record<string, string[]> = {
  first: ["Plan Selection", "Member Selection", ...commonSteps],
  second: ["Plan Selection", ...commonSteps],
  third: ["Member Selection", ...commonSteps]
};

const initialState: PlanAssignmentSlice = {
  loadingCount: 0,
  isAssignmentSuccessful: false,
  assignmentLeft: {
    planAssignment: {
      title: "Select Plan(s):",
      bullets: [
        "Select one or more Plans to assign",
        "Click 'View Details' next to a Plan to review sections and actions.",
        "If you know the name of a Plan, use the Search field or use the 'Functional Area' filter.",
        "Once you've made selections, click 'Next'."
      ]
    },
    userAssignment: {
      title: "Select Member(s):",
      bullets: [
        "Select one or more Plan Members.",
        "If you know the name of the Plan Member, use the Search field or use the filters.",
        "To select a Plan Member, check the box next to their name in the Member column.",
        "Once you've made selections, click 'Next'."
      ]
    },
    userAssignmentWithList: {
      title: "Select Member(s):",
      bullets: [
        "Select one or more Plan Members.",
        "Select a Consultant List to view the members.",
        "To select a Plan Member, check the box next to their name in the Member column.",
        "Once you've made selections, click 'Next'."
      ]
    },
    assignmentPreview: {
      title: "Confirm Selections:",
      bullets: [
        "Review the plans that are not applicable to the listed Inncodes.",
        "You can go to previous step to select different plans.",
        "Click on Proceed with Selection to go to Next Step."
      ]
    },
    assignmentDetails: {
      title: "Confirm Selections:",
      bullets: [
        "Review the Plan(s) and Plan Member(s) assignments.",
        "Add due date(s) to Plan(s).",
        "Once you've reviewed and set due dates, click 'Next'."
      ]
    },
    confirmation: {
      title: "Assign:",
      bullets: [
        "Final review or go back to make edits.",
        "Once you've finalized your review or edits, Click 'Next' to Assign Plan(s)."
      ]
    }
  },
  flow: null,
  currentStep: 0,
  planSelection: {
    list: [],
    totalList: 0,
    planDetails: {},
    filters: {
      functionalAreas: [],
      brand: [],
      region: [],
      planType: "Library Plans",
      searchText: ""
    }
  },
  memberSelection: {
    list: [],
    totalList: 0,
    filters: {
      role: [],
      property: [],
      searchText: "",
      [includeAboveProperty]: false
    }
  },
  payloadData: {
    plans: [],
    users: [],
    message: ""
  },
  isMobileDrawerVisible: false,
  isErrorWhenLimitExceed: false,
  userAssignmentTabValue: 0,
  isErrorWhenPlanSelectionExceed: false,
  userList: [],
  previewList: [],
  propertyList: [],
  isAllMemberSelected: false,
  totalMemberSelection: 0,
  unselecteMember: []
};

export const fetchPlansAsync = createAsyncThunk(`${SLICE}/fetchPlansAsync`, (data: any, thunkApi) =>
  apiRequest(LIBRARY_SERVICE_METHOD.postPlans, data, thunkApi, [], { externalHltId: data?.externalHltId || "" })
);

export const fetchTemplatePlansAsync = createAsyncThunk(`${SLICE}/fetchTemplatePlansAsync`, (data: any, thunkApi) => {
  const { pagination, ...rest } = data;
  return apiRequest(USER_PLAN_SERVICE_METHOD.postAboveProperty, rest, thunkApi, ["plans"], pagination);
});

export const fetchMembersAsync = createAsyncThunk(`${SLICE}/fetchMembersAsync`, (data: any, thunkApi) =>
  apiRequest(USER_SERVICES_METHOD.getAssociates, {}, thunkApi, [], data)
);

export const fetchPlanDetailsAsync = createAsyncThunk(`${SLICE}/fetchPlanDetailsAsync`, (data: any, thunkApi) => {
  const { planId, ...queryParams } = data;
  return apiRequest(LIBRARY_SERVICE_METHOD.getPlans, {}, thunkApi, [planId], queryParams);
});

export const fetchTemplatePlanDetailsAsync = createAsyncThunk(
  `${SLICE}/fetchPlanDetailsAsync`,
  (data: any, thunkApi) => {
    const params = {
      includePropertyInfo: data?.includePropertyInfo || false
    };
    return apiRequest(USER_PLAN_SERVICE_METHOD.getPlans, {}, thunkApi, [data.id], params);
  }
);

export const assignPlanAsync = createAsyncThunk(`${SLICE}/assignPlanAsync`, (data: any, thunkApi) =>
  apiRequest(USER_PLAN_SERVICE_METHOD.postPlanAssignment, data, thunkApi)
);

export const fetchmembersInfo = createAsyncThunk(`${SLICE}/membersInfo`, (data: any, thunkApi) =>
  apiRequest(USER_SERVICES_METHOD.getAssociates, {}, thunkApi, [], data)
);

export const fetchInncodeInfo = createAsyncThunk(`${SLICE}/inncodeInfo`, (data: any, thunkApi) =>
  apiRequest(LIBRARY_SERVICE_METHOD.postInncodeInfo, data, thunkApi)
);

export const fetchInncodeGroup = createAsyncThunk(`${SLICE}/fetchInncodeGroup`, (data: any, thunkApi) => {
  const { id, ...newData } = data;
  return apiRequest(USER_PLAN_SERVICE_METHOD.getInncodeGroups, {}, thunkApi, [id], newData);
});

export const fetchAssigneeDetailsAsync = createAsyncThunk(`${SLICE}/fetchAssigneeDetailsAsync`, (data: any, thunkApi) =>
  apiRequest(USER_SERVICES_METHOD.getAssociates, {}, thunkApi, [], data)
);

export const fetchPlanAssignmentPreviewAsync = createAsyncThunk(
  `${SLICE}/fetchPlanAssignmentPreviewAsync`,
  (data: any, thunkApi) => apiRequest(USER_PLAN_SERVICE_METHOD.postPlanAssignmentPreview, data, thunkApi)
);

export const fetchPropertyInfoAsync = createAsyncThunk(`${SLICE}/fetchPropertyInfoAsync`, (data: any, thunkApi) =>
  apiRequest(LIBRARY_SERVICE_METHOD.getPropertyInfo, {}, thunkApi, [], data)
);

export const PlanAssignment = createSlice({
  name: SLICE,
  initialState,
  reducers: {
    reset: (state) => {
      return { ...initialState, propertyList: state.propertyList };
    },
    resetPlanDetails: (state) => {
      state.planSelection.planDetails = {};
    },
    setAssignmentFlow: (state, action) => {
      const options = action.payload.withPreview
        ? flowOptions[action.payload.flow]
        : flowOptions[action.payload.flow].filter((item: any) => item !== "Assignment Preview");
      state.flow = options;
      state.currentStep = 0;
    },
    handleNext: (state) => {
      if (state?.flow) {
        if (state.currentStep < state?.flow?.length - 1) {
          state.currentStep = state.currentStep + 1;
        }
      }
    },
    handleBack: (state) => {
      if (state.currentStep > 0) {
        state.currentStep = state.currentStep - 1;
      }
    },
    setCurrentStep: (state, action) => {
      state.currentStep = action.payload;
    },
    setFilters: (state, action) => {
      state.planSelection.filters = { ...state.planSelection.filters, ...action.payload };
    },
    setMemberFilters: (state, action) => {
      state.memberSelection.filters = { ...state.memberSelection.filters, ...action.payload };
    },
    setPayloadData: (state, action) => {
      state.payloadData = {
        ...state.payloadData,
        ...action.payload
      };
    },
    setDrawerVisible: (state, action) => {
      state.isMobileDrawerVisible = action.payload;
    },
    setIsErrorWhenLimitExceed: (state, action) => {
      state.isErrorWhenLimitExceed = action.payload;
    },
    setUserAssignmentTabValue: (state, action) => {
      state.userAssignmentTabValue = action.payload;
    },
    setIsErrorWhenPlanSelectionExceed: (state, action) => {
      state.isErrorWhenPlanSelectionExceed = action.payload;
    },
    setPropertyList: (state, action) => {
      state.propertyList = action.payload;
    },
    setIsAllMemberSelected: (state, action) => {
      state.isAllMemberSelected = action.payload;
    },
    setTotalMemberSelection: (state, action) => {
      state.totalMemberSelection = action.payload;
    },
    handleUndelectedMember: (state, action) => {
      state.unselecteMember = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPlansAsync.fulfilled, (state, action: any) => {
        state.planSelection.list =
          action.meta.arg.pagination.page === 1
            ? action.payload.plans
            : [...state.planSelection.list, ...action.payload.plans];
        state.planSelection.totalList = action?.payload?.meta?.page?.total;
      })
      .addCase(fetchTemplatePlansAsync.fulfilled, (state, action: any) => {
        state.planSelection.list =
          action.meta.arg.pagination.page === 0
            ? action.payload.userplans
            : [...state.planSelection.list, ...action.payload.userplans];
        state.planSelection.totalList = action?.payload?.meta?.page?.total;
      })
      .addCase(fetchMembersAsync.fulfilled, (state, action: any) => {
        state.memberSelection.totalList = action.payload.meta.page.total;
        state.memberSelection.list =
          action.meta.arg.page === 0 ? action.payload.users : [...state.memberSelection.list, ...action.payload.users];
      })
      .addCase(fetchPlanDetailsAsync.fulfilled, (state, action: any) => {
        state.planSelection.planDetails = action.payload;
      })
      .addCase(assignPlanAsync.fulfilled, (state) => {
        state.isAssignmentSuccessful = true;
      })
      .addCase(fetchAssigneeDetailsAsync.fulfilled, (state, action) => {
        state.userList = action.payload?.users;
      })
      .addCase(fetchPlanAssignmentPreviewAsync.fulfilled, (state, action) => {
        state.previewList = action.payload?.plansNotAvailable;
      })
      .addCase(fetchPropertyInfoAsync.fulfilled, (state, action: any) => {
        const { status, ...response } = action.payload;
        state.propertyList = Object.values(response);
      })
      .addMatcher(
        (action) => isPending(action) && isPlanAssignmentThunk(action),
        (state, action) => {
          if (
            // turn on loader if api is not paginated or it is for 1st page
            !("pagination" in action.meta.arg || "page" in action.meta.arg) ||
            action.meta?.arg?.pagination === 1 ||
            action.meta?.arg?.pagination?.page === (action.meta?.arg.planType === "TEMPLATE_PLAN" ? 0 : 1) ||
            action.meta?.arg?.page === 0
          ) {
            state.loadingCount += 1;
          }
        }
      )
      .addMatcher(
        (action) => (isFulfilled(action) || isRejected(action)) && isPlanAssignmentThunk(action),
        (state, action) => {
          if (
            !("pagination" in action.meta.arg || "page" in action.meta.arg) ||
            action.meta?.arg?.pagination === 1 ||
            action.meta?.arg?.pagination?.page === (action.meta?.arg.planType === "TEMPLATE_PLAN" ? 0 : 1) ||
            action.meta?.arg?.page === 0
          ) {
            state.loadingCount -= 1;
          }
        }
      );
  }
});

export const {
  reset,
  resetPlanDetails,
  setFilters,
  setMemberFilters,
  setPayloadData,
  setAssignmentFlow,
  handleNext,
  handleBack,
  setCurrentStep,
  setDrawerVisible,
  setIsErrorWhenLimitExceed,
  setUserAssignmentTabValue,
  setIsErrorWhenPlanSelectionExceed,
  setPropertyList,
  setIsAllMemberSelected,
  setTotalMemberSelection,
  handleUndelectedMember
} = PlanAssignment.actions;

export default PlanAssignment.reducer;
